import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withValidation } from '@bingads-webui-react/validation';
import {
  Card,
  CardHeader,
  Field,
  Input,
  Label,
  makeStyles,
} from '@fluentui/react-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { maxExperimentNameLength } from '../constants/maximums';
import { errorMessages } from '../errors';
import { cardHeaderStyles } from '../page-layout';

const useStyles = makeStyles(cardHeaderStyles);

const ValidatedInput = withValidation(Input);

export const getErrorProcessor = () => {
  const customErrors = {
    required: 'NAME_REQUIRED',
    maxLength: 'NAME_TOO_LONG',
  };
  return errors => Object.entries(errors).map(([key, oldError]) => customErrors[key] || oldError);
};

export const experimentNameSchema = {
  name: 'Experiment Name Schema',
  type: 'object',
  properties: {
    experimentName: {
      type: 'string',
      required: true,
      maxLength: maxExperimentNameLength,
    },
  },
};

export const ExperimentNameCard = ({ experimentName, onChange, showError }) => {
  const styles = useStyles();
  const i18n = useContext(I18nContext);
  const learnMore = i18n.getString(_TL_('Click to learn more about'));
  const experimentNameLabel = i18n.getString(_TL_('Experiment name'));

  const [fieldProps, setFieldProps] = useState(null);

  const onValidateExperimentName = useCallback(({ isValid, messages }) => {
    if (isValid) {
      setFieldProps(null);
    } else {
      setFieldProps({
        validationMessage: i18n.getString(errorMessages[messages[0]]),
        validationState: 'error',
      });
    }
  }, [i18n]);

  const errorProcessor = useCallback(getErrorProcessor(), []);

  return (
    <Card className="experiment-name-card">
      <CardHeader
        header={
          <div className={styles.cardHeader}>
            <Label size="large" className={styles.label} required>{experimentNameLabel}</Label>
            <HelpButton
              aria-label={`${learnMore} ${experimentNameLabel}`}
              topic="pop_BA_Experiments_LiftName"
            />
          </div>
          }
      />
      <Field {...(showError ? fieldProps : null)}>
        <ValidatedInput
          type="text"
          name="experimentName"
          className="experiment-name-input"
          placeholder={i18n.getString(_TL_('Enter the name'))}
          validationProps={{
            data: { experimentName },
            errorProcessor,
            field: 'experimentName',
            onValidate: onValidateExperimentName,
            schema: experimentNameSchema,
          }}
          onChange={onChange}
        />
      </Field>
    </Card>
  );
};

ExperimentNameCard.propTypes = {
  experimentName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showError: PropTypes.bool,
};

ExperimentNameCard.defaultProps = {
  experimentName: null,
  showError: false,
};
